import { useCallback, useState } from "react";

import {
  FeatureQueryStatus,
  PreviewQueryRequestEnvironmentEnum,
  SetFeatureQueryStatusRequestEnvironmentEnum,
} from "src/clients/features-control/api";
import { toastActions } from "src/design-system/Toast/utils";
import { StatusToggleRequest } from "src/featureCatalogue/Features/StatusDropdown";
import { QueryToggleConfirmationModal } from "src/featureCatalogue/Queries/QueryToggleConfirmationModal";
import { useSetFeatureQueryStatus } from "src/featureCatalogue/queries";
import { errorMessage } from "src/utils/stringUtils";

export const useStatusToggle = () => {
  const [pendingToggle, setPendingToggle] =
    useState<StatusToggleRequest | null>(null);
  const { mutateAsync: setFeatureQueryStatus } = useSetFeatureQueryStatus();

  const handleRequestStatusChange = useCallback(
    (request: StatusToggleRequest) => {
      setPendingToggle(request);
    },
    [],
  );

  const handleConfirmStatusChange = useCallback(async () => {
    if (!pendingToggle) return;

    const { queryId, environment, isActive, etag } = pendingToggle;
    setPendingToggle(null);

    try {
      await setFeatureQueryStatus({
        id: queryId,
        environment:
          environment === PreviewQueryRequestEnvironmentEnum.SANDBOX
            ? SetFeatureQueryStatusRequestEnvironmentEnum.SANDBOX
            : SetFeatureQueryStatusRequestEnvironmentEnum.LIVE,
        status: isActive
          ? FeatureQueryStatus.INACTIVE
          : FeatureQueryStatus.ACTIVE,
        etag,
      });

      toastActions.success({
        title: isActive ? "Query Deactivated" : "Query Activated",
        description: isActive
          ? "Query is successfully deactivated."
          : "Query is successfully activated.",
      });
    } catch (error) {
      const message = errorMessage(error);
      toastActions.failure({
        title: isActive ? "Deactivation Failed" : "Activation Failed",
        description: isActive
          ? message || "Query couldn't be deactivated. Try again."
          : message || "Query couldn't be activated. Try again.",
      });
    }
  }, [pendingToggle, setFeatureQueryStatus]);

  const statusToggleModal = pendingToggle ? (
    <QueryToggleConfirmationModal
      closeModal={() => setPendingToggle(null)}
      entities={pendingToggle.entities}
      environment={pendingToggle.environment}
      event={pendingToggle.event}
      features={pendingToggle.features}
      isActive={pendingToggle.isActive}
      isOpen={!!pendingToggle}
      onConfirm={handleConfirmStatusChange}
    />
  ) : null;

  return { handleRequestStatusChange, statusToggleModal };
};
